import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {hpcAttrs} from '@github-ui/hpc'
import {useQuery} from '@github-ui/react-query'
import {Link as RouterLink, useLocation, useSearchParams} from '@github-ui/react-router'
import type {SavedViewIssuesRenderer} from '@github-ui/saved-views'
import {usePreviewQueryContext} from '@github-ui/saved-views/contexts/PreviewQueryContext'
import {addStateToQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {addTypeFilterToQuery} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {IssuesList} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import {
  defaultSortingOptions,
  issuesDashboardSortingOptions,
} from '@github-ui/shared-dashboard-lists/shared/consts/sorting-consts'
import {useNavigate} from '@github-ui/use-navigate'
import {Pagination} from '@primer/react'
import {useEffect, useMemo, useRef} from 'react'

import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {savedViewIssuesQueryOptions} from './saved-view-issues-query'

export const TanStackSavedViewIssues: SavedViewIssuesRenderer = ({owner, repo, query}) => {
  const previewQuery = usePreviewQueryContext()
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const [searchParams] = useSearchParams()
  const activeQuery = previewQuery?.currentQuery ?? query
  const page = Math.max(1, Number(searchParams.get('page')) || 1)
  const resultsQuery = useQuery(savedViewIssuesQueryOptions({owner, repo, query: activeQuery, page}))
  const issues = useMemo(() => repoIssuesToListItems(resultsQuery.data?.results ?? []), [resultsQuery.data?.results])
  const pageInfo = resultsQuery.data?.pageInfo ?? {currentPage: page, totalPages: 1, totalCount: 0}
  const resultsRef = useRef<HTMLDivElement>(null)
  const focusAfterPaginationRef = useRef(false)
  const {emptyListState} = useScreenReaderAnnouncements(
    'issues',
    resultsQuery.isFetching,
    pageInfo.currentPage,
    activeQuery,
    'created',
    pageInfo.totalCount,
    pageInfo.totalPages,
    resultsQuery.error?.message,
    undefined,
    {...defaultSortingOptions, ...issuesDashboardSortingOptions},
  )

  useEffect(() => {
    if (resultsQuery.isFetching || !focusAfterPaginationRef.current) return
    focusAfterPaginationRef.current = false
    resultsRef.current?.focus()
  }, [page, resultsQuery.isFetching])

  useEffect(() => {
    focusAfterPaginationRef.current = false
  }, [activeQuery])

  const updateQuery = (nextQuery: string) => {
    if (previewQuery) {
      previewQuery.onQueryChange(nextQuery)
      return
    }

    navigate(`${pathname}?q=${encodeURIComponent(nextQuery)}`, {preventAutofocus: true})
  }

  return (
    <div
      ref={resultsRef}
      role="group"
      aria-label="Saved view issues results"
      tabIndex={-1}
      className="tmp-mt-3"
      {...hpcAttrs()}
    >
      <IssuesList
        issues={issues}
        pageInfo={pageInfo}
        compactMode={false}
        onCompactModeChange={() => {}}
        defaultSortingKey="created"
        onSortingItemSelect={sort => updateQuery(addSortToQuery(activeQuery, sort))}
        sortingItemSelected={getSortFromQuery(activeQuery)}
        onStateFilterSelect={state => updateQuery(addStateToQuery(activeQuery, state))}
        getIssueTypeHref={issueType => `?q=${encodeURIComponent(addTypeFilterToQuery(activeQuery, issueType))}`}
        issueTypeLinkComponent={RouterLink}
        emptyListState={emptyListState}
        isLoading={resultsQuery.isLoading}
      />
      {pageInfo.totalPages > 1 && (
        <Pagination
          pageCount={pageInfo.totalPages}
          currentPage={pageInfo.currentPage}
          hrefBuilder={nextPage => `?q=${encodeURIComponent(activeQuery)}&page=${nextPage}`}
          onPageChange={(event, nextPage) => {
            // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return

            event.preventDefault()
            focusAfterPaginationRef.current = true
            const next = new URLSearchParams(searchParams)
            next.set('page', String(nextPage))
            navigate(`${pathname}?${next}`, {preventAutofocus: true})
          }}
        />
      )}
    </div>
  )
}
